import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Clock, Lock } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { FOUNDING_PERIOD_ENDED_EVENT, UPGRADE_PATH } from '../lib/foundingAccess'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { Button } from './ui/button'

/**
 * Founding-partner status in the app shell (ADR-0029).
 *
 * - Before the end date: one quiet line saying when founding access ends.
 * - After it, with no paid plan: a banner on every page and, once per page
 *   load, a dialog pointing to /subscription. The account is read-only then;
 *   views and exports keep working, so the copy says so.
 *
 * The server is the source of truth: `user.foundingPartner` comes from login
 * and GET /auth/me, and a 402 FOUNDING_PERIOD_ENDED from any write (the date
 * passing mid-session) fires FOUNDING_PERIOD_ENDED_EVENT, which flips this
 * component to the ended state without a reload.
 */
export function FoundingAccessNotice() {
  const { user } = useAuth()
  const location = useLocation()
  const access = user?.foundingPartner ?? null
  const [endedMidSession, setEndedMidSession] = useState(false)
  const ended = Boolean(access?.ended) || endedMidSession
  const onUpgradePage = location.pathname.startsWith(UPGRADE_PATH)
  // Opens once per page load (the shell mounts after the user is known), not
  // on every navigation; the banner stays.
  const [dialogOpen, setDialogOpen] = useState(ended && !onUpgradePage)

  useEffect(() => {
    const onEnded = () => {
      setEndedMidSession(true)
      setDialogOpen(true)
    }
    window.addEventListener(FOUNDING_PERIOD_ENDED_EVENT, onEnded)
    return () => window.removeEventListener(FOUNDING_PERIOD_ENDED_EVENT, onEnded)
  }, [])

  if (!access && !endedMidSession) return null

  const endedOn = access?.endsOnLabel ?? 'its end date'

  if (!ended) {
    return (
      <p className="mb-4 flex items-center text-sm text-muted-foreground" role="status" data-testid="founding-notice">
        <Clock className="h-4 w-4 mr-2 flex-shrink-0" aria-hidden="true" />
        <span>
          Founding-partner access: free through {endedOn}.{' '}
          <Link to={UPGRADE_PATH} className="underline hover:text-foreground">
            See plans
          </Link>
        </span>
      </p>
    )
  }

  return (
    <>
      <div
        className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start"
        role="alert"
        data-testid="founding-ended-banner"
      >
        <Lock className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0 text-amber-600" aria-hidden="true" />
        <div className="flex-1">
          <p className="font-medium">Your founding-partner access ended on {endedOn}.</p>
          <p className="mt-1">
            Your account is read-only: you can still view and export everything, but changes are paused until you
            choose a plan.
          </p>
        </div>
        <Link
          to={UPGRADE_PATH}
          className="ml-4 inline-flex items-center px-3 py-1.5 bg-amber-600 text-white rounded-md font-medium hover:bg-amber-700 whitespace-nowrap"
        >
          Choose a plan
        </Link>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Founding-partner access has ended</DialogTitle>
            <DialogDescription>
              Your free founding-partner period ended on {endedOn}. Nothing has been deleted: you can sign in, view and
              export every client, assessment and document. To add or change anything, choose a plan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Not now
            </Button>
            <Button asChild>
              <Link to={UPGRADE_PATH} onClick={() => setDialogOpen(false)}>
                Choose a plan
              </Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
